import { supabase } from '@/lib/supabaseClient';
import type { NotificationConsent, NotificationPreferences } from '@/types/notifications';

/**
 * Настройки уведомлений.
 *
 * Единственное место, где клиент действительно источник истины: согласие
 * писать человеку принадлежит человеку. RLS отдаёт и принимает только его
 * собственную строку, поэтому фильтр по user_id при чтении не нужен —
 * при записи он остаётся как явный адрес обновления.
 */
interface PreferencesRow {
  categories: NotificationConsent | null;
  timezone: string | null;
  quiet_hours_start: number | null;
  quiet_hours_end: number | null;
}

export async function fetchNotificationPreferences(): Promise<NotificationPreferences | null> {
  if (!supabase) {
    return null;
  }

  const { data, error } = await supabase
    .from('notification_preferences')
    .select('categories, timezone, quiet_hours_start, quiet_hours_end')
    .maybeSingle();

  if (error) {
    throw new Error('Не удалось загрузить настройки уведомлений.');
  }

  const row = data as PreferencesRow | null;

  // Строки нет — значит согласия нет: показывать выключенные переключатели
  // честнее, чем придумывать значения по умолчанию за человека.
  if (!row) {
    return null;
  }

  return {
    categories: row.categories ?? {},
    timezone: row.timezone ?? 'UTC',
    quietHours:
      row.quiet_hours_start === null || row.quiet_hours_end === null
        ? null
        : { start: row.quiet_hours_start, end: row.quiet_hours_end },
  };
}

export async function saveNotificationCategories(
  userId: string,
  categories: NotificationConsent,
): Promise<void> {
  if (!supabase) {
    return;
  }

  // Категории пишутся набором целиком: частичное слияние на клиенте
  // разошлось бы с тем, что человек видит на экране.
  const { error } = await supabase
    .from('notification_preferences')
    .update({ categories })
    .eq('user_id', userId);

  if (error) {
    throw new Error('Не удалось сохранить настройки. Попробуйте ещё раз.');
  }
}

/**
 * Сколько живых каналов доставки есть у человека.
 *
 * Нужно ровно для одного: не обещать уведомлений, которым некуда прийти.
 * Пока подписка web push не оформлена (а оформить её нечем — VAPID-ключей
 * в проекте нет), честнее сказать это прямо, чем показывать настроенные
 * переключатели и молчать.
 */
export async function countNotificationChannels(): Promise<number> {
  if (!supabase) {
    return 0;
  }

  const { count, error } = await supabase
    .from('notification_channels')
    .select('id', { count: 'exact', head: true })
    .eq('enabled', true);

  if (error) {
    throw new Error('Не удалось проверить каналы доставки.');
  }

  return count ?? 0;
}
